import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { bugsnagClient } from "../bugsnag";
import { BACKEND_URL } from "../config/config";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import SectionDetail from "./SectionDetail";
import VideoUploadWizard from "./VideoUploadWizard";
import api from "utils/api";

const SectionManagement = ({
  projectId,
  userRole,
  isGlobalAdmin,
  isChoosePlan = false,
  onChoosePlan,
  sections,
  setSections,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sectionId } = useParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newSection, setNewSection] = useState({ name: "", description: "" });
  const [selectedSection, setSelectedSection] = useState(null);

  // Get section ID from URL parameter
  const targetSection = sectionId || null;

  // Check if user can manage sections (admin or manager)
  const canManage =
    isGlobalAdmin || userRole === "admin" || userRole === "manager";

  useEffect(() => {
    if (sections.length === 0) {
      fetchSections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const data = await api.fetchSections(projectId);

      const sections = data.sections || [];

      const sectionsWithPlans = await Promise.all(
        sections.map(async (section) => {
          if (section.plan && section.plan.planId) {
            try {
              return (await api.getPlan(projectId, section._id)).section;
            } catch (err) {}
          }
          return section;
        }),
      );

      setSections(sectionsWithPlans);
      if (targetSection) {
        const section = sectionsWithPlans.find(
          (section) => section._id === targetSection,
        );
        if (section && !selectedSection) {
          setSelectedSection(section);
        }
      }
    } catch (err) {
      console.error("Fetch sections error:", err);
      setError(err.message);
      bugsnagClient.notify(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSection = async (e) => {
    e.preventDefault();

    if (!newSection.name.trim()) {
      setError(t("section_management.errors.name_required"));
      return;
    }

    try {
      const data = await api.createSection(projectId, {
        name: newSection.name.trim(),
        description: newSection.description.trim(),
      });

      trackEvent(ANALYTICS_EVENTS.SECTION_CREATED, {
        projectId,
        sectionId: data.section._id,
        sectionName: newSection.name.trim(),
      });
      setSections([...sections, data.section]);
      setNewSection({ name: "", description: "" });
      setIsCreating(false);
      setError("");
    } catch (err) {
      console.error("Create section error:", err);
      setError(err.message);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm(t("section_management.confirm_delete"))) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/projects/${projectId}/sections/${sectionId}`,
        {
          method: "DELETE",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || t("section_management.errors.delete_failed"),
        );
      }

      trackEvent(ANALYTICS_EVENTS.SECTION_DELETED, { projectId, sectionId });
      setSections(sections.filter((s) => s._id !== sectionId));
      setError("");
    } catch (err) {
      console.error("Delete section error:", err);
      setError(err.message);
      bugsnagClient.notify(err);
    }
  };

  const handleSectionClick = (section) => {
    if (isChoosePlan) {
      onChoosePlan?.(section);
      return;
    }

    // Navigate to section detail page
    navigate(`/project/${projectId}/sections/${section._id}`, {
      replace: true,
    });

    trackEvent(ANALYTICS_EVENTS.SECTION_VIEWED, {
      projectId,
      sectionId: section._id,
      sectionName: section.name,
    });
    setSelectedSection(section);
  };

  const handleBackToSections = () => {
    // Navigate back to sections list (remove sectionId from path)
    navigate(`/project/${projectId}/sections`, {
      replace: true,
    });

    setSelectedSection(null);
    fetchSections(); // Refresh data when coming back
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show individual section detail view
  if (selectedSection) {
    return (
      <SectionDetail
        section={selectedSection}
        projectId={projectId}
        userRole={userRole}
        isGlobalAdmin={isGlobalAdmin}
        onBack={handleBackToSections}
      />
    );
  }

  // Show main sections list view
  return (
    <div className="space-y-8">
      {/* Header */}
      {!isChoosePlan && (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t("section_management.title")}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t("section_management.description")}
            </p>
          </div>
          {canManage && (
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg
                className="-ml-1 mr-2 h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t("section_management.add_section")}
            </button>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create Section Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("section_management.create_new_section")}
              </h3>
              <form onSubmit={handleCreateSection} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("section_management.section_name")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newSection.name}
                    onChange={(e) =>
                      setNewSection({ ...newSection, name: e.target.value })
                    }
                    placeholder={t(
                      "section_management.section_name_placeholder",
                    )}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("section_management.description_label")}{" "}
                    <span className="text-gray-400 text-xs">
                      {t("section_management.optional")}
                    </span>
                  </label>
                  <textarea
                    value={newSection.description}
                    onChange={(e) =>
                      setNewSection({
                        ...newSection,
                        description: e.target.value,
                      })
                    }
                    placeholder={t(
                      "section_management.description_placeholder",
                    )}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreating(false);
                      setNewSection({ name: "", description: "" });
                      setError("");
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {t("section_management.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    {t("section_management.create_section_button")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sections Grid */}
      {sections.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("section_management.no_sections_title")}
          </h3>
          {!isChoosePlan && (
            <p className="mt-1 text-sm text-gray-500">
              {t("section_management.no_sections_desc")}
            </p>
          )}
          {canManage && !isChoosePlan && (
            <div className="mt-6">
              <button
                onClick={() => setIsCreating(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {t("section_management.create_first_section")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sections.map((section) => (
            <SectionCard
              key={section._id}
              section={section}
              projectId={projectId}
              canManage={canManage}
              onDelete={handleDeleteSection}
              onClick={handleSectionClick}
              isChoosePlan={isChoosePlan}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Updated Section Card Component for single plan
const SectionCard = ({
  section,
  canManage,
  onDelete,
  onClick,
  projectId,
  isChoosePlan,
}) => {
  const { t } = useTranslation();
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const hasPlan = section.plan && section.plan.planId;
  const lastActive = hasPlan
    ? new Date(section.plan.uploadDate)
    : new Date(section.createdAt);

  const getFileIcon = (mimeType, fileName) => {
    if (mimeType === "application/pdf") return "📄";
    if (fileName && fileName.toLowerCase().endsWith(".dwg")) return "📐";
    return "📋";
  };

  const handleVideoUploadClick = (e) => {
    e.stopPropagation();
    setShowVideoUpload(true);
  };

  const handleVideoUploadComplete = (_result) => {
    // You can add additional logic here to update the section data
    setShowVideoUpload(false);
  };

  const handleCloseVideoUpload = () => {
    setShowVideoUpload(false);
  };

  return (
    <>
      <div
        className="bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-lg cursor-pointer group transition-all duration-200 hover:scale-[1.02]"
        onClick={() => {
          onClick(section);
        }}
      >
        {/* Plan Preview */}
        <div className="aspect-w-16 aspect-h-12 bg-gray-100 rounded-t-lg overflow-hidden relative">
          {hasPlan && section.plan.hasPreview && section.plan.previewUrl ? (
            <div className="relative">
              <img
                src={section.plan.previewUrl}
                alt={t("section_management.plan_preview_alt", {
                  sectionName: section.name,
                })}
                className="w-full h-32 object-cover"
                crossOrigin="anonymous"
              />
              <div className="absolute bottom-2 left-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 bg-opacity-90">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-1"></span>
                  {t("section_management.preview")}
                </span>
              </div>
            </div>
          ) : hasPlan ? (
            <div className="w-full h-32 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="text-3xl mb-2">
                  {getFileIcon(section.plan.mimeType, section.plan.fileName)}
                </div>
                <p className="text-xs text-gray-500 truncate px-2">
                  {section.plan.fileName}
                </p>
                {section.plan.mimeType === "application/pdf" &&
                  !section.plan.hasPreview && (
                    <p className="text-xs text-gray-400 mt-1">
                      {t("section_management.processing")}
                    </p>
                  )}
              </div>
            </div>
          ) : (
            <div className="w-full h-32 flex items-center justify-center bg-gray-50 border-2 border-dashed border-gray-200">
              <div className="text-center">
                <svg
                  className="h-8 w-8 text-gray-400 mx-auto mb-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <p className="text-xs text-gray-500">
                  {t("section_management.no_plan_uploaded")}
                </p>
              </div>
            </div>
          )}

          {/* Overlay for actions */}
          {canManage && !isChoosePlan && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex space-x-1">
                {/* Video Upload Button */}
                <button
                  onClick={handleVideoUploadClick}
                  className="p-1 bg-white rounded-full shadow-sm hover:bg-blue-50 hover:text-blue-600"
                  title="Upload Video"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(section._id);
                  }}
                  className="p-1 bg-white rounded-full shadow-sm hover:bg-red-50 hover:text-red-600"
                  title={t("section_management.delete_section_title")}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Section Info */}
        <div className="p-4">
          <h4 className="font-medium text-gray-900 text-sm mb-1">
            {section.name}
          </h4>
          {section.description && (
            <p className="text-xs text-gray-500 mb-2 line-clamp-2">
              {section.description}
            </p>
          )}
          <p className="text-xs text-gray-500 mb-2">
            {t("section_management.last_active", {
              date: lastActive.toLocaleDateString(),
            })}
          </p>

          {/* Stats */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span className="flex items-center">
              <svg
                className="mr-1 h-3 w-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {hasPlan
                ? t("section_management.one_plan")
                : t("section_management.no_plan")}
            </span>
            {hasPlan && (
              <span className="text-green-600 text-xs font-medium">●</span>
            )}
          </div>

          {/* Click indicator */}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              {t("section_management.click_to_open")}
            </span>
            <svg
              className="h-4 w-4 text-blue-600 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
      {showVideoUpload && (
        <VideoUploadWizard
          projectId={projectId}
          sectionId={section._id}
          onClose={handleCloseVideoUpload}
          onUploadComplete={handleVideoUploadComplete}
        />
      )}
    </>
  );
};

export default SectionManagement;
